import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Autocomplete,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  OutlinedInput,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Home as HomeIcon,
  Business as ProjectIcon,
  Straighten as SizeIcon,
  Villa as VillaIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { plotService } from '../../../services/plot';
import { getProjects } from '../../../services/project';
import { globalMasterService } from '../../../services/stateAPI';
import { showSnackbar } from '../../../components/Snackbar';

const VillaForm = () => {
  const history = useHistory();
  const location = useLocation();
  const villaData = location.state?.villa;
  const isEditMode = !!villaData;

  const urlParams = new URLSearchParams(location.search);
  const urlProjectId = urlParams.get('projectId');

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [villaTypeOptions, setVillaTypeOptions] = useState([]);
  const [facingOptions, setFacingOptions] = useState([]);
  const [progressOptions, setProgressOptions] = useState([]);
  const [amenityOptions, setAmenityOptions] = useState([]);

  const [formData, setFormData] = useState({
    projectId: '',
    projectName: '',
    inventoryType: 'Villa',
    projectType: 'Villa',
    plotName: '', // This acts as Villa ID/No
    type: '', // Villa Type
    facing: '',
    width: '', // Plot Width
    length: '', // Plot Depth
    totalSize: '', // Plot Area
    builtUpArea: '',
    carpetArea: '',
    floors: '',
    rateType: 'Built-up Area',
    pricePerSqFt: '',
    companyDiscountPercent: 0,
    discountPerSqFt: 0,
    discountAmount: 0,
    plcPercent: 0,
    amount: '',
    guidelineValue: '',
    status: 'available',
    remark: '',
    amenities: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProjects();
    fetchMetadata();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const data = await getProjects({ projectType: 'Villa' });
      setProjects(data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const [vTypes, facings, progress, amenitiesRaw] = await Promise.all([
        globalMasterService.getVillaTypes ? globalMasterService.getVillaTypes() : fetchDropdown('villaType'),
        globalMasterService.getFacing ? globalMasterService.getFacing() : fetchDropdown('facing'),
        globalMasterService.getProgress ? globalMasterService.getProgress() : fetchDropdown('progress'),
        globalMasterService.getAmenities ? globalMasterService.getAmenities() : fetchDropdown('amenities')
      ]);

      setVillaTypeOptions(vTypes || []);
      setFacingOptions(facings || []);
      
      // Fixed progress options as per Villa.jsx
      setProgressOptions([
        { value: 'available', label: 'Available' },
        { value: 'booked', label: 'Booked' },
        { value: 'underconstruction', label: 'Under Construction' },
        { value: 'hold', label: 'Hold' },
      ]);

      const amenities = (amenitiesRaw || []).map(a => typeof a === 'string' ? a : a.label || a.amenitiesName || a.name || '').filter(Boolean);
      setAmenityOptions(amenities);
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  const fetchDropdown = async (type) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${API_BASE}/api/globalMasterDetails?type=${type}`);
      const result = await response.json();
      return (result.data || []).map(item => ({
        value: item._id,
        label: item.details?.[`${type}Name`] || item.details?.name || item.details?.villaType || item.details?.progressName || 'Unknown',
        ...item.details
      }));
    } catch (err) {
      return [];
    }
  };

  useEffect(() => {
    if (!isEditMode && urlProjectId && projects.length > 0) {
      const project = projects.find(p => p._id === urlProjectId || p.id === urlProjectId);
      if (project) {
        setFormData(prev => ({
          ...prev,
          projectId: project._id || project.id,
          projectName: project.projectName || project.name || ''
        }));
      }
    }
  }, [urlProjectId, projects, isEditMode]);

  useEffect(() => {
    if (isEditMode && villaData) {
      const raw = villaData._rawData || {};
      setFormData({
        projectId: raw.projectId || villaData.projectId || '',
        projectName: raw.projectName || villaData.project || '',
        inventoryType: 'Villa',
        projectType: 'Villa',
        plotName: raw.plotName || villaData.name || villaData.villaId || '',
        type: raw.villaType || villaData.type || '',
        facing: raw.facing || villaData.facing || '',
        width: raw.width || parseNumeric(villaData.plotWidth),
        length: raw.length || parseNumeric(villaData.plotDepth),
        totalSize: raw.totalSize || parseNumeric(villaData.plotArea),
        builtUpArea: raw.BuiltUpArea || raw.builtUpArea || parseNumeric(villaData.builtUpArea),
        carpetArea: raw.carpetArea || parseNumeric(villaData.carpetArea),
        floors: raw.villaFloor || raw.totalFloors || raw.floors || villaData.floors || '',
        rateType: raw.rateType || villaData.rateType || 'Built-up Area',
        pricePerSqFt: raw.pricePerSqFt || parseNumeric(villaData.rate),
        companyDiscountPercent: raw.companyDiscountPercent || parseNumeric(villaData.companyDiscountPercent || villaData.discount),
        discountPerSqFt: raw.discountPerSqFt || 0,
        discountAmount: raw.discountAmount || 0,
        plcPercent: raw.plcPercent || parseNumeric(villaData.plcPercent),
        amount: raw.amount || parseNumeric(villaData.amount),
        guidelineValue: raw.guidelineValue || parseNumeric(villaData.guidelineValue),
        status: (raw.status || villaData.status || 'available').toLowerCase(),
        remark: raw.remark || villaData.remark || '',
        amenities: Array.isArray(raw.amenities) ? raw.amenities : (Array.isArray(villaData.amenities) ? villaData.amenities : []),
      });
    }
  }, [isEditMode, villaData]);

  const parseNumeric = (val) => {
    if (typeof val === 'number') return val;
    if (!val || val === '—' || val === '') return 0;
    const numStr = val.toString().replace(/[₹,\s]/g, '').replace(/ft|sq\.ft|\/|sq\.|%/g, '').trim();
    const parsed = parseFloat(numStr);
    return isNaN(parsed) ? 0 : parsed;
  };

  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';

  const calculateAmount = (updated) => {
    const rateNum = Number(updated.pricePerSqFt || 0);
    const discountPercent = Number(updated.companyDiscountPercent || 0);
    const plcPercent = Number(updated.plcPercent || 0);
    
    let area = 0;
    if (updated.rateType === 'Carpet Area') area = Number(updated.carpetArea || 0);
    else if (updated.rateType === 'Plot Area') area = Number(updated.totalSize || 0);
    else area = Number(updated.builtUpArea || 0); // Default to Built-up Area

    const discountPerSqFt = rateNum * (discountPercent / 100);
    const discountAmount = area * discountPerSqFt;
    const discountedRate = rateNum - discountPerSqFt;
    const baseAmount = area * discountedRate;
    const plcAmount = area * rateNum * (plcPercent / 100);
    return {
      amount: (baseAmount + plcAmount).toFixed(2),
      discountPerSqFt: discountPerSqFt.toFixed(2),
      discountAmount: discountAmount.toFixed(2),
    };
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate plot area
      if (field === 'width' || field === 'length') {
        const w = Number(field === 'width' ? value : prev.width);
        const l = Number(field === 'length' ? value : prev.length);
        if (w && l) {
          updated.totalSize = (w * l).toFixed(2);
        }
      }

      // Recalculate Amount
      if (['pricePerSqFt', 'companyDiscountPercent', 'plcPercent', 'rateType', 'carpetArea', 'builtUpArea', 'totalSize', 'width', 'length'].includes(field)) {
        const { amount, discountPerSqFt, discountAmount } = calculateAmount(updated);
        updated.amount = amount;
        updated.discountPerSqFt = discountPerSqFt;
        updated.discountAmount = discountAmount;
      }

      return updated;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.projectId) newErrors.projectId = 'Project is required';
    if (!formData.plotName) newErrors.plotName = 'Villa Name/ID is required';
    if (!formData.type) newErrors.type = 'Villa Type is required';
    if (!formData.builtUpArea) newErrors.builtUpArea = 'Built-up Area is required';
    if (!formData.pricePerSqFt) newErrors.pricePerSqFt = 'Rate is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError('');
    try {
      const payload = {
        projectId: formData.projectId,
        inventoryType: 'Villa',
        projectType: 'Villa',
        plotName: formData.plotName.trim(),
        width: Number(formData.width),
        length: Number(formData.length),
        totalSize: Number(formData.totalSize),
        facing: formData.facing,
        pricePerSqFt: Number(formData.pricePerSqFt),
        plcPercent: Number(formData.plcPercent),
        companyDiscountPercent: Number(formData.companyDiscountPercent),
        discountPerSqFt: Number(formData.discountPerSqFt),
        discountAmount: Number(formData.discountAmount),
        amount: Number(formData.amount),
        guidelineValue: Number(formData.guidelineValue),
        status: formData.status.toLowerCase(),
        remark: formData.remark,
        amenities: formData.amenities,
        totalFloors: Number(formData.floors),
        villaFloor: String(formData.floors),
        BuiltUpArea: Number(formData.builtUpArea),
        carpetArea: Number(formData.carpetArea),
        villaType: formData.type, // Map it correctly for API
        rateType: formData.rateType,
      };

      let res;
      if (isEditMode) {
        res = await plotService.updatePlotInventory(villaData._rawData?._id || villaData.id, payload);
      } else {
        res = await plotService.createPlotInventory(payload);
      }

      if (res.status === true || res.success === true) {
        showSnackbar({ type: 'success', message: `Villa ${isEditMode ? 'updated' : 'added'} successfully!` });
        setTimeout(() => history.push('/app/project/ProjectEnquiry'), 1000);
      } else {
        setError(res.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => history.push('/app/project/ProjectEnquiry')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <VillaIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: 28, md: 32 } }} />
        <Typography variant="h4" fontWeight="bold" color="primary">
          {isEditMode ? 'Edit Villa' : 'Add New Villa'}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 2, boxShadow: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Section 1: Project & Villa Basic Info */}
          <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">Project & Villa Basic Info</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth required error={!!errors.projectId}>
                <InputLabel>Select Project</InputLabel>
                <Select
                  value={formData.projectId}
                  onChange={(e) => {
                    const project = projects.find(p => p._id === e.target.value);
                    handleFormChange('projectId', e.target.value);
                    if (project) handleFormChange('projectName', project.projectName);
                  }}
                  label="Select Project"
                  disabled={loadingProjects || isEditMode}
                >
                  {projects.map(p => <MenuItem key={p._id} value={p._id}>{p.projectName}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                label="Villa Name / ID"
                value={formData.plotName}
                onChange={(e) => handleFormChange('plotName', e.target.value)}
                error={!!errors.plotName}
                helperText={errors.plotName}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth required error={!!errors.type}>
                <InputLabel>Villa Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  label="Villa Type"
                >
                  {villaTypeOptions.map(opt => <MenuItem key={opt.id || opt.value} value={opt.label || opt.value}>{opt.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Facing</InputLabel>
                <Select
                  value={formData.facing}
                  onChange={(e) => handleFormChange('facing', e.target.value)}
                  label="Facing"
                >
                  {facingOptions.map(opt => <MenuItem key={opt.id || opt.value} value={opt.label || opt.value}>{opt.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Section 2: Dimensions & Areas */}
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }} gutterBottom color="primary">Dimensions & Areas</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Plot Width (ft)"
                value={formData.width}
                onChange={(e) => handleFormChange('width', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Plot Depth (ft)"
                value={formData.length}
                onChange={(e) => handleFormChange('length', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                label="Plot Area (sq.ft)"
                value={formData.totalSize}
                InputProps={{ readOnly: true }}
                sx={{ bgcolor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                type="number"
                label="Built-up Area (sq.ft)"
                value={formData.builtUpArea}
                onChange={(e) => handleFormChange('builtUpArea', e.target.value)}
                error={!!errors.builtUpArea}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Carpet Area (sq.ft)"
                value={formData.carpetArea}
                onChange={(e) => handleFormChange('carpetArea', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="No. of Floors"
                value={formData.floors}
                onChange={(e) => handleFormChange('floors', e.target.value)}
              />
            </Grid>
          </Grid>

          {/* Section 3: Pricing Details */}
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }} gutterBottom color="primary">Pricing Details</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Rate Calculated On</InputLabel>
                <Select
                  value={formData.rateType}
                  onChange={(e) => handleFormChange('rateType', e.target.value)}
                  label="Rate Calculated On"
                >
                  <MenuItem value="Built-up Area">Built-up Area</MenuItem>
                  <MenuItem value="Plot Area">Plot Area</MenuItem>
                  <MenuItem value="Carpet Area">Carpet Area</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                type="number"
                label="Rate (₹ per sq.ft)"
                value={formData.pricePerSqFt}
                onChange={(e) => handleFormChange('pricePerSqFt', e.target.value)}
                error={!!errors.pricePerSqFt}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Discount (%)"
                value={formData.companyDiscountPercent}
                onChange={(e) => handleFormChange('companyDiscountPercent', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Discount/sqft"
                value={formData.discountPerSqFt}
                InputProps={{ 
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
                sx={{ bgcolor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Discount Amount"
                value={formData.discountAmount}
                InputProps={{ 
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
                sx={{ bgcolor: '#f5f5f5' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="PLC (%)"
                value={formData.plcPercent}
                onChange={(e) => handleFormChange('plcPercent', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Total Amount (₹)"
                value={formData.amount}
                InputProps={{ 
                  readOnly: true,
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
                sx={{ bgcolor: '#e3f2fd', '& .MuiInputBase-input': { fontWeight: 'bold', color: '#1976d2' } }}
                helperText="(Discounted Amount)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Circle Rate
 (₹)"
                value={formData.guidelineValue}
                onChange={(e) => handleFormChange('guidelineValue', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
            </Grid>
          </Grid>

          {/* Section 4: Amenities & status */}
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4 }} gutterBottom color="primary">Additional Info</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                options={amenityOptions}
                value={formData.amenities}
                onChange={(e, val) => handleFormChange('amenities', val)}
                renderInput={(params) => <TextField {...params} label="Amenities" placeholder="Select Amenities" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} color="primary" size="small" />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  label="Status"
                >
                  {progressOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Remarks"
                value={formData.remark}
                onChange={(e) => handleFormChange('remark', e.target.value)}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => history.push('/app/project/ProjectEnquiry')}
              sx={{ px: 4, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              sx={{ px: 6, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {isEditMode ? 'Update Villa' : 'Save Villa'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default VillaForm;
